import dayjs from 'dayjs';
import { ROADMAP_FINANCE_STORAGE_KEY, resolveCompanyDocumentId, resolveRoadmapBusinessRole } from 'utils/roadmapFinance';
import { recalculatePassiveWorkspace } from 'utils/roadmapFinancePassive';
import { recalculateTreasuryWorkspace } from 'utils/roadmapFinanceTreasury';
import { recalculateInventoryWorkspace } from 'utils/roadmapFinanceInventory';
import { recalculateActiveWorkspace } from 'utils/roadmapFinanceActive';
import { recalculateComplianceWorkspace } from 'utils/roadmapFinanceCompliance';
import { recalculateBillingWorkspace } from 'utils/roadmapFinanceBilling';
import { recalculateTemplateEditorWorkspace, TEMPLATE_FIELD_PRESETS } from 'utils/roadmapFinanceTemplateEditor';

const companyPalette = {
    'medical-audio': '#d46b08',
    'aurifon': '#0958d9',
};

const buildCompanies = (companies = []) => {
    if (Array.isArray(companies) && companies.length > 0) {
        return companies.map((company) => ({
            id: company.id || company.documentId,
            documentId: resolveCompanyDocumentId(company),
            name: company.name || company.nome,
            alias: company.alias || (company.name || company.nome),
            color: company.color || companyPalette[resolveCompanyDocumentId(company)] || '#1677ff',
        }));
    }

    return [
        {
            id: 1,
            documentId: 'medical-audio',
            name: 'Medical Audio',
            alias: 'Società A',
            color: companyPalette['medical-audio'],
        },
        {
            id: 2,
            documentId: 'aurifon',
            name: 'Aurifon',
            alias: 'Società B',
            color: companyPalette['aurifon'],
        },
    ];
};

const createDocument = ({
    id,
    title,
    type,
    status,
    companyDocumentId,
    counterpartyName,
    patientName,
    amount,
    dueDate,
    domain,
    route,
    referenceNumber,
    tags = [],
}) => ({
    id,
    title,
    type,
    status,
    companyDocumentId,
    counterpartyName,
    patientName,
    amount,
    dueDate,
    domain,
    route,
    referenceNumber,
    tags,
    fiscalYear: dueDate ? dayjs(dueDate).year() : dayjs().year(),
    updatedAt: dayjs().subtract(Math.floor(Math.random() * 4), 'day').toISOString(),
    notes: [],
});

const PASSIVE_DOCUMENTS = [];
const PASSIVE_INVOICES = {};

const createPassiveWorkspaceSeed = () => {
    const workspace = {
        selectedInvoiceId: null,
        supplierDirectory: [
            {
                id: 'sup-deman',
                name: 'Deman Srl',
                vat: '04876540961',
                preferred: true,
            },
            {
                id: 'sup-energia-campania',
                name: 'Energia Campania',
                vat: '09123456780',
                preferred: false,
            },
        ],
        merchandiseGroups: [
            {
                id: 'protesi',
                label: 'Protesi',
                purchaseAccount: 'Protesi c/acquisti',
                salesAccount: 'Protesi c/vendite',
                defaultCostCenter: 'Aurifon Salerno',
                vatRate: 22,
            },
            {
                id: 'accessori',
                label: 'Accessori',
                purchaseAccount: 'Accessori c/acquisti',
                salesAccount: 'Accessori c/vendite',
                defaultCostCenter: 'Aurifon Salerno',
                vatRate: 22,
            },
            {
                id: 'riparazioni',
                label: 'Riparazioni',
                purchaseAccount: 'Riparazioni c/acquisti',
                salesAccount: 'Riparazioni c/vendite',
                defaultCostCenter: 'Laboratorio',
                vatRate: 22,
            },
            {
                id: 'utenze',
                label: 'Utenze',
                purchaseAccount: 'Utenze',
                salesAccount: 'Servizi',
                defaultCostCenter: 'Sede centrale',
                vatRate: 22,
            },
            {
                id: 'spedizione',
                label: 'Spese accessorie',
                purchaseAccount: 'Spese di trasporto',
                salesAccount: 'Riadddebiti',
                defaultCostCenter: 'Sede centrale',
                vatRate: 22,
            },
        ],
        articleCatalog: [
            {
                code: 'OTI-INTENT',
                label: 'Oticon Intent',
                aliases: ['Oticon Intent livello 3', 'Oticon Intent livello 2', 'Oticon Intent'],
                groupId: 'protesi',
                purchaseAccount: 'Protesi c/acquisti',
                defaultCostCenter: 'Aurifon Salerno',
            },
            {
                code: 'CHG-FAST',
                label: 'Charger Fast',
                aliases: ['Charger Fast'],
                groupId: 'accessori',
                purchaseAccount: 'Accessori c/acquisti',
                defaultCostCenter: 'Aurifon Salerno',
            },
            {
                code: 'LAB-REPAIR',
                label: 'Riparazione laboratorio',
                aliases: ['Riparazione laboratorio'],
                groupId: 'riparazioni',
                purchaseAccount: 'Riparazioni c/acquisti',
                defaultCostCenter: 'Laboratorio',
            },
            {
                code: 'ENERGIA-MAR',
                label: 'Fornitura energia Marzo',
                aliases: ['Fornitura energia Marzo'],
                groupId: 'utenze',
                purchaseAccount: 'Utenze',
                defaultCostCenter: 'Sede centrale',
            },
            {
                code: 'SPESE-SPED',
                label: 'Spese spedizione',
                aliases: ['Spese trasporto DHL', 'Spese spedizione'],
                groupId: 'spedizione',
                purchaseAccount: 'Spese di trasporto',
                defaultCostCenter: 'Sede centrale',
            },
        ],
        ddts: [],
        returnDdts: [
            {
                id: 'return-ddt-7232',
                originalDdtId: 'ddt-7232',
                supplierId: 'sup-deman',
                companyDocumentId: 'aurifon',
                internalNumber: 'RES-CAR-7232',
                returnedAt: dayjs().subtract(4, 'day').format('YYYY-MM-DD'),
                submittedAt: dayjs().subtract(4, 'day').toISOString(),
                status: 'inviato',
                lines: [
                    {
                        id: 'return-ddt-7232-ddt-7232-line-1',
                        articleCode: 'OTI-INTENT',
                        description: 'Oticon Intent livello 3 – rientro (articolo non conforme)',
                        groupId: 'protesi',
                        quantity: 1,
                        unitPrice: 975,
                        discount: 0.34,
                        vatRate: 22,
                    },
                ],
                linkedCreditNoteId: null,
                note: 'Reso per articolo non conforme alle specifiche ordinate. Comunicato al commerciale Deman.',
            },
        ],
        invoices: PASSIVE_INVOICES,
        salesEntries: [
            {
                id: 'sale-2026-01-protesi-1',
                companyDocumentId: 'aurifon',
                groupId: 'protesi',
                groupLabel: 'Protesi',
                account: 'Protesi c/vendite',
                description: 'Vendita Oticon Intent livello 2 – Rosa De Luca',
                invoiceNumber: 'FT-001/2026',
                date: dayjs().subtract(3, 'month').startOf('month').add(14, 'day').format('YYYY-MM-DD'),
                year: dayjs().subtract(3, 'month').year(),
                month: dayjs().subtract(3, 'month').month() + 1,
                net: 2800.00,
                vatRate: 22,
                vat: 616.00,
                gross: 3416.00,
            },
            {
                id: 'sale-2026-01-accessori-1',
                companyDocumentId: 'aurifon',
                groupId: 'accessori',
                groupLabel: 'Accessori',
                account: 'Accessori c/vendite',
                description: 'Vendita kit accessori – Mario Esposito',
                invoiceNumber: 'FT-002/2026',
                date: dayjs().subtract(3, 'month').startOf('month').add(20, 'day').format('YYYY-MM-DD'),
                year: dayjs().subtract(3, 'month').year(),
                month: dayjs().subtract(3, 'month').month() + 1,
                net: 145.00,
                vatRate: 22,
                vat: 31.90,
                gross: 176.90,
            },
            {
                id: 'sale-2026-02-protesi-1',
                companyDocumentId: 'aurifon',
                groupId: 'protesi',
                groupLabel: 'Protesi',
                account: 'Protesi c/vendite',
                description: 'Vendita Phonak Audeo Fit – Carmela Ferraro',
                invoiceNumber: 'FT-003/2026',
                date: dayjs().subtract(2, 'month').startOf('month').add(8, 'day').format('YYYY-MM-DD'),
                year: dayjs().subtract(2, 'month').year(),
                month: dayjs().subtract(2, 'month').month() + 1,
                net: 3200.00,
                vatRate: 22,
                vat: 704.00,
                gross: 3904.00,
            },
            {
                id: 'sale-2026-02-protesi-2',
                companyDocumentId: 'aurifon',
                groupId: 'protesi',
                groupLabel: 'Protesi',
                account: 'Protesi c/vendite',
                description: 'Vendita Oticon Intent livello 3 – Giuseppe Russo',
                invoiceNumber: 'FT-004/2026',
                date: dayjs().subtract(2, 'month').startOf('month').add(22, 'day').format('YYYY-MM-DD'),
                year: dayjs().subtract(2, 'month').year(),
                month: dayjs().subtract(2, 'month').month() + 1,
                net: 3850.00,
                vatRate: 22,
                vat: 847.00,
                gross: 4697.00,
            },
            {
                id: 'sale-2026-02-riparazioni-1',
                companyDocumentId: 'aurifon',
                groupId: 'riparazioni',
                groupLabel: 'Riparazioni',
                account: 'Riparazioni c/vendite',
                description: 'Riparazione guscio – Luigi Coppola',
                invoiceNumber: 'FT-005/2026',
                date: dayjs().subtract(2, 'month').startOf('month').add(15, 'day').format('YYYY-MM-DD'),
                year: dayjs().subtract(2, 'month').year(),
                month: dayjs().subtract(2, 'month').month() + 1,
                net: 280.00,
                vatRate: 22,
                vat: 61.60,
                gross: 341.60,
            },
            {
                id: 'sale-2026-03-protesi-1',
                companyDocumentId: 'aurifon',
                groupId: 'protesi',
                groupLabel: 'Protesi',
                account: 'Protesi c/vendite',
                description: 'Vendita Signia Pure Charge – Anna Cuomo',
                invoiceNumber: 'FT-006/2026',
                date: dayjs().subtract(1, 'month').startOf('month').add(5, 'day').format('YYYY-MM-DD'),
                year: dayjs().subtract(1, 'month').year(),
                month: dayjs().subtract(1, 'month').month() + 1,
                net: 2600.00,
                vatRate: 22,
                vat: 572.00,
                gross: 3172.00,
            },
            {
                id: 'sale-2026-03-accessori-1',
                companyDocumentId: 'aurifon',
                groupId: 'accessori',
                groupLabel: 'Accessori',
                account: 'Accessori c/vendite',
                description: 'Accessori batterie e tubi – varie pratiche',
                invoiceNumber: 'FT-007/2026',
                date: dayjs().subtract(1, 'month').startOf('month').add(18, 'day').format('YYYY-MM-DD'),
                year: dayjs().subtract(1, 'month').year(),
                month: dayjs().subtract(1, 'month').month() + 1,
                net: 220.00,
                vatRate: 22,
                vat: 48.40,
                gross: 268.40,
            },
            {
                id: 'sale-2026-03-riparazioni-1',
                companyDocumentId: 'aurifon',
                groupId: 'riparazioni',
                groupLabel: 'Riparazioni',
                account: 'Riparazioni c/vendite',
                description: 'Intervento laboratorio standard – Gennaro Iaccarino',
                invoiceNumber: 'FT-008/2026',
                date: dayjs().subtract(1, 'month').startOf('month').add(25, 'day').format('YYYY-MM-DD'),
                year: dayjs().subtract(1, 'month').year(),
                month: dayjs().subtract(1, 'month').month() + 1,
                net: 320.00,
                vatRate: 22,
                vat: 70.40,
                gross: 390.40,
            },
            {
                id: 'sale-2026-04-protesi-1',
                companyDocumentId: 'aurifon',
                groupId: 'protesi',
                groupLabel: 'Protesi',
                account: 'Protesi c/vendite',
                description: 'Vendita Starkey Evolv AI – Francesco De Rosa',
                invoiceNumber: 'FT-009/2026',
                date: dayjs().subtract(5, 'day').format('YYYY-MM-DD'),
                year: dayjs().year(),
                month: dayjs().month() + 1,
                net: 4200.00,
                vatRate: 22,
                vat: 924.00,
                gross: 5124.00,
            },
            {
                id: 'sale-2026-04-accessori-2',
                companyDocumentId: 'aurifon',
                groupId: 'accessori',
                groupLabel: 'Accessori',
                account: 'Accessori c/vendite',
                description: 'Charger Fast + tubing kit – Francesco De Rosa',
                invoiceNumber: 'FT-010/2026',
                date: dayjs().subtract(5, 'day').format('YYYY-MM-DD'),
                year: dayjs().year(),
                month: dayjs().month() + 1,
                net: 185.00,
                vatRate: 22,
                vat: 40.70,
                gross: 225.70,
            },
        ],
    };

    return recalculatePassiveWorkspace(workspace);
};

const createTreasuryWorkspaceSeed = ({ passiveWorkspace }) => {
    return recalculateTreasuryWorkspace({
        passiveWorkspace,
        treasuryWorkspace: {
            activeSourceId: 'source-bank-intesa',
            activePayableId: 'payable-deman-2025-02',
            activeMovementId: 'movement-1',
            selectedLedgerSupplier: 'Deman Srl',
            sources: [
                {
                    id: 'source-bank-intesa',
                    companyDocumentId: 'aurifon',
                    name: 'Banca Intesa',
                    type: 'banca',
                    alias: 'Conto operativo Aurifon',
                    openingBalance: 13240.55,
                    actualBalance: 11337.01,
                },
                {
                    id: 'source-card-michelangelo',
                    companyDocumentId: 'aurifon',
                    name: 'Carta Michelangelo',
                    type: 'carta',
                    alias: 'Carta aziendale titolare',
                    openingBalance: 980,
                    actualBalance: 621.3,
                },
                {
                    id: 'source-cassa-aurifon',
                    companyDocumentId: 'aurifon',
                    name: 'Cassa',
                    type: 'cassa',
                    alias: 'Cassa front office',
                    openingBalance: 420,
                    actualBalance: 420,
                },
                {
                    id: 'source-posta-medical',
                    companyDocumentId: 'medical-audio',
                    name: 'Posta',
                    type: 'posta',
                    alias: 'Conto Poste Medical Audio',
                    openingBalance: 2100,
                    actualBalance: 1840,
                },
            ],
            payables: [
                {
                    id: 'payable-deman-2024-01',
                    companyDocumentId: 'aurifon',
                    supplierName: 'Deman Srl',
                    label: 'Fattura fornitore 1201',
                    total: 3850.00,
                    issueDate: '2024-02-10',
                    dueDate: '2024-03-10',
                    year: 2024,
                    sourceKind: 'invoice',
                    creditIds: [],
                    paymentHistory: [
                        {
                            id: 'payment-deman-2024-01a',
                            sourceId: 'source-bank-intesa',
                            sourceLabel: 'Banca Intesa',
                            amount: 3850.00,
                            date: '2024-03-08',
                            valueDate: '2024-03-08',
                            note: 'Saldo fattura 1201',
                        },
                    ],
                    appliedCredits: [],
                },
                {
                    id: 'payable-deman-2024-02',
                    companyDocumentId: 'aurifon',
                    supplierName: 'Deman Srl',
                    label: 'Fattura fornitore 1345',
                    total: 2240.00,
                    issueDate: '2024-07-15',
                    dueDate: '2024-08-15',
                    year: 2024,
                    sourceKind: 'invoice',
                    creditIds: [],
                    paymentHistory: [
                        {
                            id: 'payment-deman-2024-02a',
                            sourceId: 'source-bank-intesa',
                            sourceLabel: 'Banca Intesa',
                            amount: 1200.00,
                            date: '2024-08-12',
                            valueDate: '2024-08-12',
                            note: 'Acconto fattura 1345',
                        },
                        {
                            id: 'payment-deman-2024-02b',
                            sourceId: 'source-bank-intesa',
                            sourceLabel: 'Banca Intesa',
                            amount: 1040.00,
                            date: '2024-09-05',
                            valueDate: '2024-09-05',
                            note: 'Saldo fattura 1345',
                        },
                    ],
                    appliedCredits: [],
                },
                {
                    id: 'payable-deman-2025-01',
                    companyDocumentId: 'aurifon',
                    supplierName: 'Deman Srl',
                    label: 'Fattura fornitore 1590',
                    total: 4100.00,
                    issueDate: '2025-01-20',
                    dueDate: '2025-02-20',
                    year: 2025,
                    sourceKind: 'invoice',
                    creditIds: ['credit-deman-2025-01'],
                    paymentHistory: [
                        {
                            id: 'payment-deman-2025-01a',
                            sourceId: 'source-bank-intesa',
                            sourceLabel: 'Banca Intesa',
                            amount: 4100.00,
                            date: '2025-02-18',
                            valueDate: '2025-02-18',
                            note: 'Saldo fattura 1590',
                        },
                    ],
                    appliedCredits: [],
                },
                {
                    id: 'payable-deman-2025-02',
                    companyDocumentId: 'aurifon',
                    supplierName: 'Deman Srl',
                    label: 'Fattura fornitore 1720',
                    total: 1875.50,
                    issueDate: '2025-09-10',
                    dueDate: '2025-10-10',
                    year: 2025,
                    sourceKind: 'invoice',
                    creditIds: [],
                    paymentHistory: [
                        {
                            id: 'payment-deman-2025-02a',
                            sourceId: 'source-bank-intesa',
                            sourceLabel: 'Banca Intesa',
                            amount: 1875.50,
                            date: '2025-10-08',
                            valueDate: '2025-10-08',
                            note: 'Saldo fattura 1720',
                        },
                    ],
                    appliedCredits: [],
                },
                {
                    id: 'payable-foreign-starkey-2025',
                    foreignInvoiceId: 'foreign-inv-demo-3',
                    companyDocumentId: 'aurifon',
                    supplierName: 'Starkey Hearing Technologies',
                    label: 'Fattura estera STARKEY-2025-0889',
                    total: 2947.20,
                    foreignCurrency: 'USD',
                    foreignAmount: 3200.00,
                    exchangeRate: 0.921,
                    issueDate: dayjs().subtract(45, 'day').format('YYYY-MM-DD'),
                    dueDate: dayjs().subtract(15, 'day').format('YYYY-MM-DD'),
                    year: 2025,
                    sourceKind: 'foreign_invoice',
                    creditIds: [],
                    paymentHistory: [
                        {
                            id: 'payment-starkey-2025-01',
                            sourceId: 'source-bank-intesa',
                            sourceLabel: 'Banca Intesa',
                            amount: 2947.20,
                            foreignAmount: 3200.00,
                            foreignCurrency: 'USD',
                            date: dayjs().subtract(38, 'day').format('YYYY-MM-DD'),
                            valueDate: dayjs().subtract(38, 'day').format('YYYY-MM-DD'),
                            movementId: null,
                            note: 'Bonifico estero USD – STARKEY-2025-0889',
                        },
                    ],
                    appliedCredits: [],
                },
            ],
            supplierCredits: [
                {
                    id: 'credit-deman-2025-01',
                    companyDocumentId: 'aurifon',
                    supplierName: 'Deman Srl',
                    label: 'Nota di credito fornitore NC-31',
                    total: 320,
                    usedAmount: 0,
                    issuedAt: '2025-03-15',
                    year: 2025,
                },
                {
                    id: 'credit-deman-01',
                    companyDocumentId: 'aurifon',
                    supplierName: 'Deman Srl',
                    label: 'Nota di credito fornitore NC-44',
                    total: 420,
                    usedAmount: 0,
                    issuedAt: dayjs().subtract(8, 'day').format('YYYY-MM-DD'),
                    year: 2026,
                },
            ],
            bankMovements: [
                {
                    id: 'movement-1',
                    sourceId: 'source-bank-intesa',
                    companyDocumentId: 'aurifon',
                    date: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
                    bookingDate: dayjs().format('YYYY-MM-DD'),
                    description: 'Bonifico Deman Srl acconto fornitore',
                    counterpartyName: 'Deman Srl',
                    amount: -500,
                    importedBatchId: 'import-seed-1',
                    reconciledPayableId: null,
                },
                {
                    id: 'movement-2',
                    sourceId: 'source-bank-intesa',
                    companyDocumentId: 'aurifon',
                    date: dayjs().format('YYYY-MM-DD'),
                    bookingDate: dayjs().format('YYYY-MM-DD'),
                    description: 'Bonifico Energia Campania',
                    counterpartyName: 'Energia Campania',
                    amount: -503.52,
                    importedBatchId: 'import-seed-1',
                    reconciledPayableId: null,
                },
                {
                    id: 'movement-3',
                    sourceId: 'source-card-michelangelo',
                    companyDocumentId: 'aurifon',
                    date: dayjs().subtract(2, 'day').format('YYYY-MM-DD'),
                    bookingDate: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
                    description: 'Addebito Google Workspace',
                    counterpartyName: 'Google',
                    amount: -114,
                    importedBatchId: 'import-seed-2',
                    reconciledPayableId: null,
                    suspectedMissingDocument: true,
                },
            ],
            imports: [
                {
                    id: 'import-seed-1',
                    sourceId: 'source-bank-intesa',
                    fileName: 'estratto-conto-marzo.csv',
                    importedAt: dayjs().toISOString(),
                    linesCount: 2,
                },
                {
                    id: 'import-seed-2',
                    sourceId: 'source-card-michelangelo',
                    fileName: 'carta-michelangelo-marzo.xlsx',
                    importedAt: dayjs().toISOString(),
                    linesCount: 1,
                },
            ],
        },
    });
};

const createInventoryWorkspaceSeed = () => {
    return recalculateInventoryWorkspace({
        activeDepositId: 'dep-centrale-salerno',
        selectedSerialId: 'serial-oticon-001',
        articles: [
            { code: 'OTI-INTENT', label: 'Oticon Intent', supplierName: 'Deman Srl', minStock: 2, preferredSupplier: 'Deman Srl', obsolete: false },
            { code: 'CHG-FAST', label: 'Charger Fast', supplierName: 'Deman Srl', minStock: 3, preferredSupplier: 'Deman Srl', obsolete: false },
            { code: 'LAB-TUBE', label: 'Tubicino laboratorio', supplierName: 'Lab Service', minStock: 12, preferredSupplier: 'Lab Service', obsolete: false },
            { code: 'LEGACY-BATT', label: 'Batteria serie legacy', supplierName: 'Legacy Supplier', minStock: 0, preferredSupplier: 'Legacy Supplier', obsolete: true },
        ],
        deposits: [
            { id: 'dep-centrale-salerno', name: 'Deposito Centrale Salerno', type: 'centrale', companyDocumentId: 'aurifon' },
            { id: 'dep-napoli', name: 'Sede Napoli', type: 'sede', companyDocumentId: 'aurifon' },
            { id: 'dep-giovanni-van', name: 'Furgone Giovanni', type: 'tecnico', companyDocumentId: 'aurifon' },
            { id: 'dep-laboratorio', name: 'Laboratorio', type: 'laboratorio', companyDocumentId: 'aurifon' },
            { id: 'dep-patient-rosa', name: 'Contovisione Rosa De Luca', type: 'contovisione', companyDocumentId: 'aurifon' },
        ],
        serials: [
            {
                id: 'serial-oticon-001',
                articleCode: 'OTI-INTENT',
                serialNumber: 'SN-OTI-001',
                currentDepositId: 'dep-centrale-salerno',
                status: 'disponibile',
                patientName: null,
                trialStartedAt: null,
                history: [
                    { id: 'h-1', date: dayjs().subtract(40, 'day').format('YYYY-MM-DD'), action: 'Carico fornitore', depositName: 'Deposito Centrale Salerno', operator: 'Gloria', note: 'Bolla Deman 7046' },
                ],
            },
            {
                id: 'serial-oticon-002',
                articleCode: 'OTI-INTENT',
                serialNumber: 'SN-OTI-002',
                currentDepositId: 'dep-patient-rosa',
                status: 'in_prova',
                patientName: 'Rosa De Luca',
                trialStartedAt: dayjs().subtract(37, 'day').format('YYYY-MM-DD'),
                history: [
                    { id: 'h-2', date: dayjs().subtract(70, 'day').format('YYYY-MM-DD'), action: 'Carico fornitore', depositName: 'Deposito Centrale Salerno', operator: 'Gloria', note: 'Bolla Deman 7232' },
                    { id: 'h-3', date: dayjs().subtract(37, 'day').format('YYYY-MM-DD'), action: 'DDT contovisione', depositName: 'Contovisione Rosa De Luca', operator: 'Ida', note: 'Prova 30 giorni' },
                ],
            },
            {
                id: 'serial-charge-001',
                articleCode: 'CHG-FAST',
                serialNumber: 'SN-CHG-001',
                currentDepositId: 'dep-giovanni-van',
                status: 'disponibile',
                patientName: null,
                trialStartedAt: null,
                history: [
                    { id: 'h-4', date: dayjs().subtract(12, 'day').format('YYYY-MM-DD'), action: 'Trasferimento interno', depositName: 'Furgone Giovanni', operator: 'Gloria', note: 'Kit demo' },
                ],
            },
            {
                id: 'serial-legacy-001',
                articleCode: 'LEGACY-BATT',
                serialNumber: 'SN-LEG-001',
                currentDepositId: 'dep-laboratorio',
                status: 'disponibile',
                patientName: null,
                trialStartedAt: null,
                history: [
                    { id: 'h-5', date: dayjs().subtract(420, 'day').format('YYYY-MM-DD'), action: 'Carico storico', depositName: 'Laboratorio', operator: 'Sistema', note: 'Articolo legacy' },
                ],
            },
        ],
        transfers: [
            {
                id: 'transfer-1',
                date: dayjs().subtract(12, 'day').format('YYYY-MM-DD'),
                fromDepositId: 'dep-centrale-salerno',
                toDepositId: 'dep-giovanni-van',
                serialIds: ['serial-charge-001'],
                operator: 'Gloria',
                note: 'Demo tecnico',
            },
        ],
        consumptions: [
            {
                id: 'cons-1',
                articleCode: 'LAB-TUBE',
                depositId: 'dep-laboratorio',
                quantity: 2,
                date: dayjs().subtract(3, 'day').format('YYYY-MM-DD'),
                operator: 'Tecnico Lab',
                note: 'Riparazione guscio',
            },
        ],
        reorderProposals: [],
    });
};

const createForeignInvoiceWorkspaceSeed = () => ({
    activeForeignInvoiceId: 'foreign-inv-demo-1',
    foreignInvoices: [
        {
            id: 'foreign-inv-demo-1',
            referenceNumber: 'PHONAK-2026-0341',
            supplierName: 'Phonak GmbH',
            supplierCountry: 'DE',
            supplierVat: 'DE811197721',
            currency: 'EUR',
            issueDate: dayjs().subtract(8, 'day').format('YYYY-MM-DD'),
            totalAmountForeign: 5200.00,
            exchangeRate: 1,
            totalAmountEur: 5200.00,
            vatRegime: 'inversione_contabile',
            purchaseAccount: 'Protesi c/acquisti',
            costCenter: 'Aurifon Salerno',
            companyDocumentId: 'aurifon',
            linkedCustomsDeclarationIds: [],
            status: 'da_contabilizzare',
            notes: 'Importazione protesi acustiche serie Marvel, ordine Q1/2026.',
            createdAt: dayjs().subtract(8, 'day').toISOString(),
        },
        {
            id: 'foreign-inv-demo-2',
            referenceNumber: 'WIDEX-US-2026-0112',
            supplierName: 'Widex USA Inc.',
            supplierCountry: 'US',
            supplierVat: 'US94-3342567',
            currency: 'USD',
            issueDate: dayjs().subtract(20, 'day').format('YYYY-MM-DD'),
            totalAmountForeign: 8750.00,
            exchangeRate: 0.924,
            totalAmountEur: 8085.00,
            vatRegime: 'non_imponibile',
            purchaseAccount: 'Protesi c/acquisti',
            costCenter: 'Aurifon Salerno',
            companyDocumentId: 'aurifon',
            linkedCustomsDeclarationIds: ['customs-demo-1'],
            status: 'da_contabilizzare',
            notes: 'Importazione extra-UE con bolletta doganale MXP.',
            createdAt: dayjs().subtract(20, 'day').toISOString(),
        },
        {
            id: 'foreign-inv-demo-3',
            referenceNumber: 'STARKEY-2025-0889',
            supplierName: 'Starkey Hearing Technologies',
            supplierCountry: 'US',
            supplierVat: 'US41-1563868',
            currency: 'USD',
            issueDate: dayjs().subtract(45, 'day').format('YYYY-MM-DD'),
            totalAmountForeign: 3200.00,
            exchangeRate: 0.921,
            totalAmountEur: 2947.20,
            vatRegime: 'non_imponibile',
            purchaseAccount: 'Protesi c/acquisti',
            costCenter: 'Aurifon Salerno',
            companyDocumentId: 'aurifon',
            linkedCustomsDeclarationIds: ['customs-demo-2'],
            status: 'contabilizzata',
            committedAt: dayjs().subtract(40, 'day').toISOString(),
            notes: 'Contabilizzata. Bolletta doganale NAP/2025/3344.',
            createdAt: dayjs().subtract(45, 'day').toISOString(),
        },
    ],
    customsDeclarations: [
        {
            id: 'customs-demo-1',
            declarationNumber: 'MXP/2026/004421',
            declarationDate: dayjs().subtract(15, 'day').format('YYYY-MM-DD'),
            portOfEntry: 'Dogana Malpensa',
            goodsDescription: 'Apparecchi acustici digitali - cod. doganale 9021.40',
            customsValue: 8085.00,
            dutiesAmount: 0,
            vatAmount: 1778.70,
            clearanceCosts: [
                { id: 'cc-demo-1', description: 'Spese spedizioniere doganale', amount: 220.00 },
                { id: 'cc-demo-2', description: 'Diritti doganali fissi', amount: 60.00 },
                { id: 'cc-demo-3', description: 'Magazzinaggio aeroportuale', amount: 85.00 },
            ],
            linkedInvoiceId: 'foreign-inv-demo-2',
        },
        {
            id: 'customs-demo-2',
            declarationNumber: 'NAP/2025/003344',
            declarationDate: dayjs().subtract(42, 'day').format('YYYY-MM-DD'),
            portOfEntry: 'Dogana Napoli',
            goodsDescription: 'Apparecchi acustici - cod. doganale 9021.40',
            customsValue: 2947.20,
            dutiesAmount: 0,
            vatAmount: 648.38,
            clearanceCosts: [
                { id: 'cc-demo-4', description: 'Spese spedizioniere', amount: 180.00 },
                { id: 'cc-demo-5', description: 'Diritti doganali', amount: 45.00 },
            ],
            linkedInvoiceId: 'foreign-inv-demo-3',
        },
    ],
});

const createActiveWorkspaceSeed = () => {
    return recalculateActiveWorkspace({
        activeAgreementId: 'agreement-rosa-001',
        activeQueueItemId: 'queue-item-001',
        activeCreditNoteId: null,
        agreements: [
            {
                id: 'agreement-rosa-001',
                companyDocumentId: 'aurifon',
                patientName: 'Rosa De Luca',
                patientCode: 'DLCRSO75D45F839X',
                serialId: 'serial-oticon-002',
                serialNumber: 'SN-OTI-002',
                articleCode: 'OTI-INTENT',
                articleLabel: 'Oticon Intent livello 3',
                dealType: 'asl',
                price: 2300.00,
                discount: 0,
                aslQuota: 1151.00,
                privateQuota: 1149.00,
                paymentMethod: 'rateizzazione',
                installments: [
                    { id: 'inst-1', dueDate: dayjs().add(30, 'day').format('YYYY-MM-DD'), amount: 383.00, paid: false },
                    { id: 'inst-2', dueDate: dayjs().add(60, 'day').format('YYYY-MM-DD'), amount: 383.00, paid: false },
                    { id: 'inst-3', dueDate: dayjs().add(90, 'day').format('YYYY-MM-DD'), amount: 383.00, paid: false },
                ],
                status: 'inviato_amministrazione',
                linkedDocumentId: 'doc-practice-aur-08',
                createdAt: dayjs().subtract(37, 'day').toISOString(),
                confirmedAt: dayjs().subtract(2, 'day').toISOString(),
                notes: 'Accordo chiuso dopo 37 giorni di prova. Quota ASL verificata con l\'amministrazione.',
            },
            {
                id: 'agreement-gennaro-001',
                companyDocumentId: 'aurifon',
                patientName: 'Gennaro Iaccarino',
                patientCode: 'CCRGNN68M10F839P',
                serialId: 'serial-oticon-001',
                serialNumber: 'SN-OTI-001',
                articleCode: 'OTI-INTENT',
                articleLabel: 'Oticon Intent livello 2',
                dealType: 'privato',
                price: 1800.00,
                discount: 0.05,
                aslQuota: 0,
                privateQuota: 1710.00,
                paymentMethod: 'bonifico',
                installments: [],
                status: 'confermato',
                linkedDocumentId: null,
                createdAt: dayjs().subtract(5, 'day').toISOString(),
                confirmedAt: dayjs().subtract(1, 'day').toISOString(),
                notes: 'Acquisto diretto privato. Sconto 5% fedeltà applicato.',
            },
            {
                id: 'agreement-ferraro-001',
                companyDocumentId: 'medical-audio',
                patientName: 'Carlo Ferraro',
                patientCode: 'FRRCRL65T10F839W',
                serialId: null,
                serialNumber: null,
                articleCode: 'SIGNIA-PURE',
                articleLabel: 'Signia Pure Charge&Go AX (bilaterale)',
                dealType: 'asl',
                price: 2302.00,
                discount: 0,
                aslQuota: 1151.00,
                privateQuota: 1151.00,
                paymentMethod: 'bonifico',
                installments: [],
                status: 'confermato',
                linkedDocumentId: 'doc-practice-med-01',
                createdAt: dayjs().subtract(14, 'day').toISOString(),
                confirmedAt: dayjs().subtract(7, 'day').toISOString(),
                notes: 'Accordo su protesi bilaterale. Paziente seguito da Medical Audio. Da migrare ad Aurifon prima della fatturazione ASL.',
            },
            {
                id: 'agreement-leone-001',
                companyDocumentId: 'medical-audio',
                patientName: 'Teresa Leone',
                patientCode: 'LNETRS82P43F839V',
                serialId: null,
                serialNumber: null,
                articleCode: 'OTI-MORE',
                articleLabel: 'Oticon More livello 1',
                dealType: 'privato',
                price: 1600.00,
                discount: 0,
                aslQuota: 0,
                privateQuota: 1600.00,
                paymentMethod: 'contanti',
                installments: [],
                status: 'bozza',
                linkedDocumentId: null,
                createdAt: dayjs().subtract(3, 'day').toISOString(),
                confirmedAt: null,
                notes: 'Acquisita da Medical Audio. Da trasferire ad Aurifon prima della fatturazione.',
            },
            {
                id: 'agreement-anna-001',
                companyDocumentId: 'medical-audio',
                patientName: 'Anna Cuomo',
                patientCode: 'CMUANA72R41F839T',
                serialId: null,
                serialNumber: null,
                articleCode: 'SIGNIA-PURE',
                articleLabel: 'Signia Pure Charge&Go AX',
                dealType: 'assicurazione',
                price: 2800.00,
                discount: 0,
                aslQuota: 0,
                privateQuota: 2800.00,
                paymentMethod: 'finanziamento',
                installments: [],
                status: 'fatturato',
                linkedDocumentId: 'doc-credit-2026-09',
                createdAt: dayjs().subtract(62, 'day').toISOString(),
                confirmedAt: dayjs().subtract(55, 'day').toISOString(),
                notes: 'Pratica assicurativa completata. Nota di credito emessa per rimborso parziale.',
            },
        ],
        operativeQueue: [
            {
                id: 'queue-item-001',
                companyDocumentId: 'aurifon',
                title: 'Pratica ASL Rosa De Luca – da fatturare',
                patientName: 'Rosa De Luca',
                amount: 1151.00,
                priority: 'urgente',
                status: 'lavorazione',
                fromRole: 'commerciale',
                toRole: 'contabile',
                linkedAgreementId: 'agreement-rosa-001',
                linkedDocumentId: 'doc-practice-aur-08',
                note: 'Accordo confermato. In attesa di emissione fattura ASL.',
                createdAt: dayjs().subtract(2, 'day').toISOString(),
            },
            {
                id: 'queue-item-002',
                companyDocumentId: 'aurifon',
                title: 'Pratica privato Gennaro Iaccarino – bonifico atteso',
                patientName: 'Gennaro Iaccarino',
                amount: 1710.00,
                priority: 'normale',
                status: 'in_attesa',
                fromRole: 'commerciale',
                toRole: 'contabile',
                linkedAgreementId: 'agreement-gennaro-001',
                linkedDocumentId: null,
                note: 'Accordo confermato. Attendere bonifico prima di emettere fattura.',
                createdAt: dayjs().subtract(1, 'day').toISOString(),
            },
            {
                id: 'queue-item-003',
                companyDocumentId: 'aurifon',
                title: 'Corrispettivi mese corrente – export cassa',
                patientName: null,
                amount: 510.60,
                priority: 'normale',
                status: 'in_attesa',
                fromRole: 'commerciale',
                toRole: 'contabile',
                linkedAgreementId: null,
                linkedDocumentId: null,
                note: 'Corrispettivi incassati in contanti da inviare per quadratura registro.',
                createdAt: dayjs().toISOString(),
            },
        ],
        creditNotes: [
            {
                id: 'credit-note-active-001',
                companyDocumentId: 'medical-audio',
                referenceInvoiceId: null,
                referenceInvoiceNumber: 'FT-MA-006/2025',
                patientName: 'Anna Cuomo',
                reason: 'Rimborso parziale pratica assicurativa – protocollo ins. 2025/4421',
                lines: [
                    {
                        id: 'cn-line-1',
                        description: 'Storno pratica Signia Pure Charge&Go AX',
                        quantity: 1,
                        unitPrice: 2690.00,
                        vatRate: 22,
                        vatAmount: 591.80,
                        lineTotal: 3281.80,
                    },
                ],
                amount: 2690.00,
                vatRate: 22,
                vatAmount: 591.80,
                total: 3281.80,
                status: 'emessa',
                emittedAt: dayjs().subtract(2, 'day').format('YYYY-MM-DD'),
                linkedReInvoiceId: null,
                notes: 'Nota di credito emessa per storno totale della fattura originale su indicazione dell\'assicurazione.',
            },
        ],
        reInvoices: [
            {
                id: 're-invoice-001',
                companyDocumentId: 'aurifon',
                linkedCreditNoteId: null,
                patientName: 'Rosa De Luca',
                reason: 'Rifatturazione con aggiornamento quota ASL dopo revisione tariffario 2026',
                lines: [
                    {
                        id: 'ri-line-1',
                        description: 'Oticon Intent livello 3 – quota privato ricalcolata',
                        quantity: 1,
                        unitPrice: 1149.00,
                        vatRate: 22,
                        vatAmount: 252.78,
                        lineTotal: 1401.78,
                    },
                ],
                amount: 1149.00,
                vatRate: 22,
                vatAmount: 252.78,
                total: 1401.78,
                status: 'bozza',
                emittedAt: null,
                notes: 'In attesa di conferma quota definitiva da parte del commerciale.',
            },
        ],
        receipts: [
            {
                id: 'receipt-001',
                companyDocumentId: 'aurifon',
                receiptType: 'smart_receipt',
                date: dayjs().subtract(3, 'day').format('YYYY-MM-DD'),
                lines: [
                    {
                        id: 'rec-line-1',
                        description: 'Batterie Audfil misura 13 – confezione da 6',
                        quantity: 2,
                        unitPrice: 12.50,
                        vatRate: 22,
                    },
                    {
                        id: 'rec-line-2',
                        description: 'Kit tubicini standard',
                        quantity: 1,
                        unitPrice: 8.00,
                        vatRate: 22,
                    },
                ],
                paymentMethod: 'contanti',
                amount: 33.00,
                vatAmount: 7.26,
                total: 40.26,
                customerName: 'Cliente anonimo',
                notes: '',
                status: 'emesso',
                emittedAt: dayjs().subtract(3, 'day').toISOString(),
                sdiTransmission: { status: 'transmitted', transmittedAt: dayjs().subtract(3, 'day').add(1, 'hour').toISOString() },
            },
            {
                id: 'receipt-002',
                companyDocumentId: 'aurifon',
                receiptType: 'smart_receipt',
                date: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
                lines: [
                    {
                        id: 'rec2-line-1',
                        description: 'Charger Fast',
                        quantity: 1,
                        unitPrice: 120.00,
                        vatRate: 22,
                    },
                    {
                        id: 'rec2-line-2',
                        description: 'Garanzia estesa 2 anni',
                        quantity: 1,
                        unitPrice: 35.00,
                        vatRate: 22,
                    },
                ],
                paymentMethod: 'pos',
                amount: 155.00,
                vatAmount: 34.10,
                total: 189.10,
                customerName: 'Mario Rossi',
                notes: 'Acquisto con tessera fedeltà',
                status: 'emesso',
                emittedAt: dayjs().subtract(1, 'day').toISOString(),
                sdiTransmission: { status: 'transmitted', transmittedAt: dayjs().subtract(1, 'day').add(30, 'minute').toISOString() },
            },
            {
                id: 'receipt-003',
                companyDocumentId: 'aurifon',
                receiptType: 'vendita_banco',
                date: dayjs().format('YYYY-MM-DD'),
                lines: [
                    {
                        id: 'rec3-line-1',
                        description: 'Accessori vari – vendita banco',
                        quantity: 1,
                        unitPrice: 22.00,
                        vatRate: 22,
                    },
                ],
                paymentMethod: 'contanti',
                amount: 22.00,
                vatAmount: 4.84,
                total: 26.84,
                customerName: '',
                notes: '',
                status: 'bozza',
                emittedAt: null,
                sdiTransmission: { status: 'not_required', transmittedAt: null },
            },
            {
                id: 'receipt-004',
                companyDocumentId: 'aurifon',
                receiptType: 'smart_receipt',
                date: dayjs().subtract(5, 'day').format('YYYY-MM-DD'),
                lines: [
                    {
                        id: 'rec4-line-1',
                        description: 'Apparecchio acustico Oticon More – accessorio',
                        quantity: 1,
                        unitPrice: 85.00,
                        vatRate: 4,
                    },
                ],
                paymentMethod: 'pos',
                amount: 85.00,
                vatAmount: 3.40,
                total: 88.40,
                customerName: 'Lucia Ferrari',
                notes: '',
                status: 'emesso',
                emittedAt: dayjs().subtract(5, 'day').toISOString(),
                sdiTransmission: { status: 'error', transmittedAt: null },
            },
            {
                id: 'receipt-005',
                companyDocumentId: 'aurifon',
                receiptType: 'vendita_banco',
                date: dayjs().subtract(2, 'day').format('YYYY-MM-DD'),
                lines: [
                    {
                        id: 'rec5-line-1',
                        description: 'Gel per calchi orecchio – 50ml',
                        quantity: 3,
                        unitPrice: 9.50,
                        vatRate: 22,
                    },
                    {
                        id: 'rec5-line-2',
                        description: 'Panno microfibra pulizia',
                        quantity: 2,
                        unitPrice: 4.00,
                        vatRate: 22,
                    },
                ],
                paymentMethod: 'contanti',
                amount: 36.50,
                vatAmount: 8.03,
                total: 44.53,
                customerName: 'Cliente anonimo',
                notes: 'Vendita consumabili',
                status: 'emesso',
                emittedAt: dayjs().subtract(2, 'day').toISOString(),
                sdiTransmission: { status: 'not_required', transmittedAt: null },
            },
            {
                id: 'receipt-006',
                companyDocumentId: 'aurifon',
                receiptType: 'smart_receipt',
                date: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
                lines: [
                    {
                        id: 'rec6-line-1',
                        description: 'Desitin – batterie 312',
                        quantity: 4,
                        unitPrice: 6.00,
                        vatRate: 22,
                    },
                ],
                paymentMethod: 'contanti',
                amount: 24.00,
                vatAmount: 5.28,
                total: 29.28,
                customerName: 'Giovanni Bianchi',
                notes: '',
                status: 'annullato',
                emittedAt: dayjs().subtract(7, 'day').toISOString(),
                cancelledAt: dayjs().subtract(6, 'day').toISOString(),
                cancellationReason: 'Errore importo, riemesso correttamente',
                sdiTransmission: { status: 'transmitted', transmittedAt: dayjs().subtract(7, 'day').add(20, 'minute').toISOString() },
            },
        ],
        customerLedgers: [
            {
                id: 'ledger-rosa',
                companyDocumentId: 'aurifon',
                patientName: 'Rosa De Luca',
                patientCode: 'DLCRSO75D45F839X',
                entries: [
                    {
                        id: 'entry-1',
                        date: dayjs().subtract(2, 'day').format('YYYY-MM-DD'),
                        description: 'Acconto accordo Oticon Intent',
                        debit: 0,
                        credit: 300.00,
                        balance: -300.00,
                    },
                ],
                openBalance: -300.00,
            },
        ],
    });
};

const createComplianceWorkspaceSeed = () => {
    return recalculateComplianceWorkspace({
        activeNsoOrderId: 'nso-order-441',
        activePraticaId: 'pratica-asl-001',
        activeFatturaPAId: 'fattura-pa-001',
        activeStsExportId: 'sts-q1-2026',
        nsoOrders: [
            {
                id: 'nso-order-441',
                companyDocumentId: 'aurifon',
                nsoNumber: 'NSO-441',
                aslName: 'ASL Napoli 1',
                patientName: 'Gennaro Iaccarino',
                patientCf: 'CCRGNN68M10F839P',
                articleCode: 'OTI-INTENT',
                articleDescription: 'Apparecchio acustico Oticon Intent livello 2',
                requestedQuantity: 1,
                requestedAmount: 1151.00,
                status: 'preso_in_carico',
                receivedAt: dayjs().subtract(3, 'day').format('YYYY-MM-DD'),
                processedAt: null,
                linkedPraticaId: null,
                notes: 'Ordine NSO ricevuto via SDI. In attesa di apertura pratica ASL.',
            },
            {
                id: 'nso-order-389',
                companyDocumentId: 'aurifon',
                nsoNumber: 'NSO-389',
                aslName: 'ASL Napoli 3 Sud',
                patientName: 'Rosa De Luca',
                patientCf: 'DLCRSO75D45F839X',
                articleCode: 'OTI-INTENT',
                articleDescription: 'Apparecchio acustico Oticon Intent livello 3',
                requestedQuantity: 1,
                requestedAmount: 1151.00,
                status: 'evaso',
                receivedAt: dayjs().subtract(40, 'day').format('YYYY-MM-DD'),
                processedAt: dayjs().subtract(38, 'day').format('YYYY-MM-DD'),
                linkedPraticaId: 'pratica-asl-001',
                notes: 'Ordine evaso. Pratica ASL aperta e collegata.',
            },
            {
                id: 'nso-order-502',
                companyDocumentId: 'medical-audio',
                nsoNumber: 'NSO-502',
                aslName: 'ASL Salerno 1',
                patientName: 'Carlo Ferraro',
                patientCf: 'FRRCRL65T10F839W',
                articleCode: 'SIGNIA-PURE',
                articleDescription: 'Apparecchio acustico Signia Pure Charge&Go AX',
                requestedQuantity: 2,
                requestedAmount: 2302.00,
                status: 'ricevuto',
                receivedAt: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
                processedAt: null,
                linkedPraticaId: null,
                notes: '',
            },
        ],
        pratiche: [
            {
                id: 'pratica-asl-001',
                companyDocumentId: 'aurifon',
                patientName: 'Rosa De Luca',
                patientCf: 'DLCRSO75D45F839X',
                aslName: 'ASL Napoli 3 Sud',
                aslCodiceUnivoco: 'A9ZT8S',
                linkedNsoOrderId: 'nso-order-389',
                linkedAgreementId: 'agreement-rosa-001',
                aslQuota: 1151.00,
                privateQuota: 1149.00,
                splitPayment: true,
                quotaResiduaCarico: 'cittadino',
                riconducibileLabel: '',
                status: 'in_prova',
                linkedFatturaPAId: null,
                openedAt: dayjs().subtract(38, 'day').format('YYYY-MM-DD'),
                ordineRicevutoAt: dayjs().subtract(38, 'day').format('YYYY-MM-DD'),
                provaAt: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
                consegnaAt: null,
                fatturatoAt: null,
                pagatoAt: null,
                closedAt: null,
                notes: 'Pratica collegata ad ordine NSO-389. Apparecchio in prova dal paziente.',
            },
            {
                id: 'pratica-asl-002',
                companyDocumentId: 'aurifon',
                patientName: 'Mario Esposito',
                patientCf: 'SPSMRO71A20F839K',
                aslName: 'ASL Napoli 1',
                aslCodiceUnivoco: 'A9ZT8S',
                linkedNsoOrderId: null,
                linkedAgreementId: null,
                aslQuota: 1151.00,
                privateQuota: 800.00,
                splitPayment: true,
                quotaResiduaCarico: 'riconducibile',
                riconducibileLabel: 'INAIL',
                status: 'pagato',
                linkedFatturaPAId: 'fattura-pa-002',
                openedAt: dayjs().subtract(90, 'day').format('YYYY-MM-DD'),
                ordineRicevutoAt: dayjs().subtract(90, 'day').format('YYYY-MM-DD'),
                provaAt: dayjs().subtract(85, 'day').format('YYYY-MM-DD'),
                consegnaAt: dayjs().subtract(75, 'day').format('YYYY-MM-DD'),
                fatturatoAt: dayjs().subtract(65, 'day').format('YYYY-MM-DD'),
                pagatoAt: dayjs().subtract(60, 'day').format('YYYY-MM-DD'),
                closedAt: dayjs().subtract(60, 'day').format('YYYY-MM-DD'),
                notes: 'Pratica completata. Quota residua a carico INAIL. Pagamento ASL ricevuto.',
            },
            {
                id: 'pratica-asl-003',
                companyDocumentId: 'aurifon',
                patientName: 'Lucia Bianchi',
                patientCf: 'BNCLCU80E44F839M',
                aslName: 'ASL Napoli 3 Sud',
                aslCodiceUnivoco: 'A9ZT8S',
                linkedNsoOrderId: null,
                linkedAgreementId: null,
                aslQuota: 1151.00,
                privateQuota: 600.00,
                splitPayment: true,
                quotaResiduaCarico: 'cittadino',
                riconducibileLabel: '',
                status: 'ordine_ricevuto',
                linkedFatturaPAId: null,
                openedAt: dayjs().subtract(10, 'day').format('YYYY-MM-DD'),
                ordineRicevutoAt: dayjs().subtract(10, 'day').format('YYYY-MM-DD'),
                provaAt: null,
                consegnaAt: null,
                fatturatoAt: null,
                pagatoAt: null,
                closedAt: null,
                notes: 'Ordine ricevuto. In attesa di avvio prova.',
            },
            {
                id: 'pratica-asl-004',
                companyDocumentId: 'aurifon',
                patientName: 'Antonio Ferraro',
                patientCf: 'FRRNTN72C18F839J',
                aslName: 'ASL Salerno 1',
                aslCodiceUnivoco: 'B2ZK9T',
                linkedNsoOrderId: null,
                linkedAgreementId: null,
                aslQuota: 2302.00,
                privateQuota: 500.00,
                splitPayment: false,
                quotaResiduaCarico: 'cittadino',
                riconducibileLabel: '',
                status: 'consegnato',
                linkedFatturaPAId: null,
                openedAt: dayjs().subtract(55, 'day').format('YYYY-MM-DD'),
                ordineRicevutoAt: dayjs().subtract(55, 'day').format('YYYY-MM-DD'),
                provaAt: dayjs().subtract(50, 'day').format('YYYY-MM-DD'),
                consegnaAt: dayjs().subtract(20, 'day').format('YYYY-MM-DD'),
                fatturatoAt: null,
                pagatoAt: null,
                closedAt: null,
                notes: 'Apparecchi consegnati. In attesa di emissione fattura PA.',
            },
            {
                id: 'pratica-asl-005',
                companyDocumentId: 'medical-audio',
                patientName: 'Carmela Russo',
                patientCf: 'RSSCML69P55F839Q',
                aslName: 'ASL Napoli 2 Nord',
                aslCodiceUnivoco: 'C4ZM1R',
                linkedNsoOrderId: null,
                linkedAgreementId: null,
                aslQuota: 1151.00,
                privateQuota: 900.00,
                splitPayment: true,
                quotaResiduaCarico: 'cittadino',
                riconducibileLabel: '',
                status: 'fatturato',
                linkedFatturaPAId: 'fattura-pa-001',
                openedAt: dayjs().subtract(70, 'day').format('YYYY-MM-DD'),
                ordineRicevutoAt: dayjs().subtract(70, 'day').format('YYYY-MM-DD'),
                provaAt: dayjs().subtract(65, 'day').format('YYYY-MM-DD'),
                consegnaAt: dayjs().subtract(45, 'day').format('YYYY-MM-DD'),
                fatturatoAt: dayjs().subtract(15, 'day').format('YYYY-MM-DD'),
                pagatoAt: null,
                closedAt: null,
                notes: 'Fattura PA inviata. In attesa di pagamento ASL.',
            },
        ],
        fatturePA: [
            {
                id: 'fattura-pa-001',
                companyDocumentId: 'aurifon',
                praticaId: 'pratica-asl-001',
                patientName: 'Rosa De Luca',
                aslName: 'ASL Napoli 3 Sud',
                aslCodiceUnivoco: 'A9ZT8S',
                referenceNumber: 'FPA-AUR-001/2026',
                amount: 1151.00,
                vatRate: 4,
                vatAmount: 46.04,
                total: 1197.04,
                cig: 'Z3A1B2C3D4',
                cup: '',
                rifOrdineNumero: 'NSO-389',
                rifOrdineData: dayjs().subtract(40, 'day').format('YYYY-MM-DD'),
                rifOrdineCodice: '',
                nsoNumero: 'NSO-389',
                nsoData: dayjs().subtract(40, 'day').format('YYYY-MM-DD'),
                nsoTipoDocumento: 'ORDINEPA',
                splitPayment: true,
                quotaResiduaCarico: 'cittadino',
                riconducibileLabel: '',
                privateQuota: 1149.00,
                status: 'bozza',
                emittedAt: null,
                xmlPreview: '<FatturaElettronica xmlns="http://ivaservizi.agenziaentrate.gov.it/docs/xsd/fatture/v1.2" versione="FPA12"><FatturaElettronicaHeader><!-- bozza mock --></FatturaElettronicaHeader></FatturaElettronica>',
                notes: 'Fattura PA generata da pratica ASL-001. In attesa di invio SDI.',
                createdAt: dayjs().subtract(2, 'day').toISOString(),
            },
            {
                id: 'fattura-pa-002',
                companyDocumentId: 'aurifon',
                praticaId: 'pratica-asl-002',
                patientName: 'Mario Esposito',
                aslName: 'ASL Napoli 1',
                aslCodiceUnivoco: 'A9ZT8S',
                referenceNumber: 'FPA-AUR-012/2025',
                amount: 1151.00,
                vatRate: 4,
                vatAmount: 46.04,
                total: 1197.04,
                cig: 'Z9F4E5G6H7',
                cup: 'I12J3456K789',
                rifOrdineNumero: 'ORD-2025-088',
                rifOrdineData: dayjs().subtract(65, 'day').format('YYYY-MM-DD'),
                rifOrdineCodice: 'ACQ-PROT',
                nsoNumero: '',
                nsoData: '',
                nsoTipoDocumento: '',
                splitPayment: true,
                quotaResiduaCarico: 'riconducibile',
                riconducibileLabel: 'INAIL',
                privateQuota: 800.00,
                status: 'accettata',
                emittedAt: dayjs().subtract(62, 'day').format('YYYY-MM-DD'),
                xmlPreview: null,
                notes: 'Fattura PA accettata da SDI. Pratica chiusa.',
                createdAt: dayjs().subtract(65, 'day').toISOString(),
            },
        ],
        stsExports: [
            {
                id: 'sts-q1-2026',
                companyDocumentId: 'aurifon',
                period: 'Q1 2026',
                entries: [
                    { id: 'sts-e-1', patientName: 'Rosa De Luca', patientCf: 'DLCRSO75D45F839X', amount: 1149.00, date: dayjs().subtract(38, 'day').format('YYYY-MM-DD'), deductible: true },
                    { id: 'sts-e-2', patientName: 'Gennaro Iaccarino', patientCf: 'CCRGNN68M10F839P', amount: 1710.00, date: dayjs().subtract(5, 'day').format('YYYY-MM-DD'), deductible: true },
                    { id: 'sts-e-3', patientName: 'Anna Cuomo', patientCf: 'CMUANA72R41F839T', amount: 110.00, date: dayjs().subtract(20, 'day').format('YYYY-MM-DD'), deductible: false },
                ],
                totalEntries: 3,
                totalAmount: 2969.00,
                status: 'pronta_export',
                exportedAt: null,
            },
            {
                id: 'sts-q4-2025',
                companyDocumentId: 'aurifon',
                period: 'Q4 2025',
                entries: [
                    { id: 'sts-e-4', patientName: 'Mario Esposito', patientCf: 'SPSMRO71A20F839K', amount: 800.00, date: dayjs().subtract(95, 'day').format('YYYY-MM-DD'), deductible: true },
                ],
                totalEntries: 1,
                totalAmount: 800.00,
                status: 'inviata',
                exportedAt: dayjs().subtract(80, 'day').toISOString(),
            },
        ],
        archiveEntries: [],
        accountantExports: [
            {
                id: 'acc-export-001',
                companyDocumentId: 'aurifon',
                type: 'sts_trimestrale',
                period: 'Q1 2026',
                status: 'pronto',
                generatedAt: dayjs().subtract(1, 'day').toISOString(),
                entries: [],
            },
            {
                id: 'acc-export-002',
                companyDocumentId: 'aurifon',
                type: 'pratiche_asl',
                period: '2025',
                status: 'inviato',
                generatedAt: dayjs().subtract(80, 'day').toISOString(),
                entries: [],
            },
            {
                id: 'acc-export-003',
                companyDocumentId: 'aurifon',
                type: 'fatture_pa',
                period: '2025',
                status: 'inviato',
                generatedAt: dayjs().subtract(78, 'day').toISOString(),
                entries: [],
            },
        ],
    });
};

const createBillingWorkspaceSeed = () => {
    return recalculateBillingWorkspace({
        activeBillingChannel: 'b2c',
        activeB2bInvoiceId: 'b2b-inv-002',
        activeFreeInvoiceId: null,
        registriSezionali: [
            {
                id: 'registro-b2c-aurifon',
                channel: 'b2c',
                companyDocumentId: 'aurifon',
                label: 'Corrispettivi privati',
                prefix: 'CORR',
                padDigits: 4,
                yearSuffix: true,
                startingNumber: 1,
                currentNumber: 12,
                fiscalYear: dayjs().year(),
                active: true,
                notes: 'Registro corrispettivi per clienti privati – Aurifon.',
            },
            {
                id: 'registro-b2b-aurifon',
                channel: 'b2b',
                companyDocumentId: 'aurifon',
                label: 'Fatture aziende',
                prefix: 'FT-B2B',
                padDigits: 3,
                yearSuffix: true,
                startingNumber: 1,
                currentNumber: 4,
                fiscalYear: dayjs().year(),
                active: true,
                notes: 'Registro principale fatture verso aziende – Aurifon.',
            },
            {
                id: 'registro-b2g-aurifon',
                channel: 'b2g',
                companyDocumentId: 'aurifon',
                label: 'Fatture PA / ASL',
                prefix: 'FPA',
                padDigits: 3,
                yearSuffix: true,
                startingNumber: 1,
                currentNumber: 3,
                fiscalYear: dayjs().year(),
                active: true,
                notes: 'Registro fatture verso Pubblica Amministrazione e ASL – Aurifon.',
            },
            {
                id: 'registro-b2b-medical-audio',
                channel: 'b2b',
                companyDocumentId: 'medical-audio',
                label: 'Fatture aziende',
                prefix: 'FT-B2B',
                padDigits: 3,
                yearSuffix: true,
                startingNumber: 1,
                currentNumber: 1,
                fiscalYear: dayjs().year(),
                active: true,
                notes: 'Registro fatture verso aziende – Medical Audio.',
            },
            {
                id: 'registro-b2g-medical-audio',
                channel: 'b2g',
                companyDocumentId: 'medical-audio',
                label: 'Fatture PA / ASL',
                prefix: 'FPA',
                padDigits: 3,
                yearSuffix: true,
                startingNumber: 1,
                currentNumber: 0,
                fiscalYear: dayjs().year(),
                active: false,
                notes: '',
            },
        ],
        b2bInvoices: [
            {
                id: 'b2b-inv-001',
                companyDocumentId: 'aurifon',
                referenceNumber: 'FT-B2B-001/2026',
                issueDate: dayjs().subtract(45, 'day').format('YYYY-MM-DD'),
                recipient: {
                    businessName: 'Clinica Audiologica Sud Srl',
                    vatNumber: '07654321098',
                    sdiCode: 'ABCDEF1',
                    pec: null,
                    address: 'Via Toledo 45, 80134 Napoli',
                },
                lines: [
                    {
                        id: 'b2b-inv-001-line-1',
                        description: 'Consulenza audiologica specialistica – 3 sessioni',
                        quantity: 3,
                        unitPrice: 180.00,
                        vatRate: 22,
                        lineTotal: 540.00,
                    },
                    {
                        id: 'b2b-inv-001-line-2',
                        description: 'Noleggio attrezzatura diagnostica',
                        quantity: 1,
                        unitPrice: 320.00,
                        vatRate: 22,
                        lineTotal: 320.00,
                    },
                ],
                netAmount: 860.00,
                vatAmount: 189.20,
                totalAmount: 1049.20,
                paymentTerms: 'net30',
                dueDate: dayjs().subtract(15, 'day').format('YYYY-MM-DD'),
                status: 'pagata',
                sdiStatus: 'consegnata',
                issuedAt: dayjs().subtract(45, 'day').toISOString(),
                notes: '',
            },
            {
                id: 'b2b-inv-002',
                companyDocumentId: 'aurifon',
                referenceNumber: 'FT-B2B-002/2026',
                issueDate: dayjs().subtract(10, 'day').format('YYYY-MM-DD'),
                recipient: {
                    businessName: 'Ottica & Udito Campania Srl',
                    vatNumber: '03987654321',
                    sdiCode: 'XYZ1234',
                    pec: 'fatture@oticaudito.pec.it',
                    address: 'Corso Umberto I 112, 80138 Napoli',
                },
                lines: [
                    {
                        id: 'b2b-inv-002-line-1',
                        description: 'Fornitura apparecchi acustici Oticon Intent – 2 unità',
                        quantity: 2,
                        unitPrice: 1800.00,
                        vatRate: 22,
                        lineTotal: 3600.00,
                    },
                ],
                netAmount: 3600.00,
                vatAmount: 792.00,
                totalAmount: 4392.00,
                paymentTerms: 'net60',
                dueDate: dayjs().add(50, 'day').format('YYYY-MM-DD'),
                status: 'consegnata',
                sdiStatus: 'consegnata',
                issuedAt: dayjs().subtract(10, 'day').toISOString(),
                notes: 'Ordine di acquisto rivenditore OAU-2026-055.',
            },
            {
                id: 'b2b-inv-003',
                companyDocumentId: 'medical-audio',
                referenceNumber: 'FT-B2B-003/2026',
                issueDate: dayjs().subtract(3, 'day').format('YYYY-MM-DD'),
                recipient: {
                    businessName: 'Studio Medico Associato Vesuvio',
                    vatNumber: '08112233445',
                    sdiCode: null,
                    pec: 'amministrazione@studiomedicovestuvio.pec.it',
                    address: 'Piazza Garibaldi 7, 80055 Portici',
                },
                lines: [
                    {
                        id: 'b2b-inv-003-line-1',
                        description: 'Servizio di audiometria strumentale – contratto annuale',
                        quantity: 1,
                        unitPrice: 2400.00,
                        vatRate: 22,
                        lineTotal: 2400.00,
                    },
                ],
                netAmount: 2400.00,
                vatAmount: 528.00,
                totalAmount: 2928.00,
                paymentTerms: 'net30',
                dueDate: dayjs().add(27, 'day').format('YYYY-MM-DD'),
                status: 'emessa',
                sdiStatus: 'in_elaborazione',
                issuedAt: dayjs().subtract(3, 'day').toISOString(),
                notes: '',
            },
            {
                id: 'b2b-inv-004',
                companyDocumentId: 'aurifon',
                referenceNumber: null,
                issueDate: null,
                recipient: {
                    businessName: '',
                    vatNumber: '',
                    sdiCode: '',
                    pec: '',
                    address: '',
                },
                lines: [
                    {
                        id: 'b2b-inv-004-line-1',
                        description: '',
                        quantity: 1,
                        unitPrice: 0,
                        vatRate: 22,
                        lineTotal: 0,
                    },
                ],
                netAmount: 0,
                vatAmount: 0,
                totalAmount: 0,
                paymentTerms: 'net30',
                dueDate: null,
                status: 'bozza',
                sdiStatus: null,
                issuedAt: null,
                notes: '',
            },
        ],
        freeInvoices: [
            {
                id: 'free-inv-001',
                companyDocumentId: 'aurifon',
                channel: 'b2b',
                referenceNumber: 'FT-B2B-005/2026',
                issueDate: dayjs().subtract(20, 'day').format('YYYY-MM-DD'),
                recipient: {
                    name: '',
                    businessName: 'ASP Riabilitazione Campania Srl',
                    fiscalCode: '',
                    vatNumber: '06543210987',
                    address: 'Via Mergellina 22, 80122 Napoli',
                    sdiCode: 'GHIJKL2',
                    pec: '',
                    codiceUnivoco: '',
                },
                lines: [
                    {
                        id: 'free-inv-001-line-1',
                        description: 'Fornitura apparecchio acustico – intervento urgente fuori turno',
                        quantity: 1,
                        unitPrice: 1200.00,
                        vatRate: 22,
                        lineTotal: 1200.00,
                    },
                ],
                netAmount: 1200.00,
                vatAmount: 264.00,
                totalAmount: 1464.00,
                paymentTerms: 'net30',
                dueDate: dayjs().add(10, 'day').format('YYYY-MM-DD'),
                status: 'emessa',
                reconciliationStatus: 'da_riconciliare',
                reconciliation: null,
                registroId: 'registro-b2b-aurifon',
                issuedAt: dayjs().subtract(20, 'day').toISOString(),
                notes: 'Fattura emessa fuori flusso per intervento urgente. Riconciliare con la matricola consegnata.',
            },
            {
                id: 'free-inv-002',
                companyDocumentId: 'aurifon',
                channel: 'b2c',
                referenceNumber: 'FT-LIB-001/2026',
                issueDate: dayjs().subtract(8, 'day').format('YYYY-MM-DD'),
                recipient: {
                    name: 'Mario Esposito',
                    businessName: '',
                    fiscalCode: 'SPSMRA80A01F839Y',
                    vatNumber: '',
                    address: 'Via Caracciolo 5, 80121 Napoli',
                    sdiCode: '',
                    pec: '',
                    codiceUnivoco: '',
                },
                lines: [
                    {
                        id: 'free-inv-002-line-1',
                        description: 'Revisione e taratura apparecchio acustico',
                        quantity: 1,
                        unitPrice: 180.00,
                        vatRate: 22,
                        lineTotal: 180.00,
                    },
                ],
                netAmount: 180.00,
                vatAmount: 39.60,
                totalAmount: 219.60,
                paymentTerms: 'immediato',
                dueDate: dayjs().subtract(8, 'day').format('YYYY-MM-DD'),
                status: 'emessa',
                reconciliationStatus: 'riconciliata',
                reconciliation: {
                    linkedType: 'pratica',
                    linkedId: 'pratica-asl-001',
                    linkedLabel: 'Pratica – Mario Esposito (ASL)',
                    reconciledAt: dayjs().subtract(5, 'day').toISOString(),
                    note: 'Collegata alla pratica ASL del paziente.',
                },
                registroId: null,
                issuedAt: dayjs().subtract(8, 'day').toISOString(),
                notes: '',
            },
            {
                id: 'free-inv-003',
                companyDocumentId: 'aurifon',
                channel: 'b2b',
                referenceNumber: null,
                issueDate: null,
                recipient: {
                    name: '',
                    businessName: '',
                    fiscalCode: '',
                    vatNumber: '',
                    address: '',
                    sdiCode: '',
                    pec: '',
                    codiceUnivoco: '',
                },
                lines: [
                    {
                        id: 'free-inv-003-line-1',
                        description: '',
                        quantity: 1,
                        unitPrice: 0,
                        vatRate: 22,
                        lineTotal: 0,
                    },
                ],
                netAmount: 0,
                vatAmount: 0,
                totalAmount: 0,
                paymentTerms: 'net30',
                dueDate: null,
                status: 'bozza',
                reconciliationStatus: null,
                reconciliation: null,
                registroId: null,
                issuedAt: null,
                notes: '',
            },
        ],
        activeDdts: [
            {
                id: 'act-ddt-001',
                companyDocumentId: 'aurifon',
                ddtNumber: `DDT-${dayjs().year()}-001`,
                issueDate: dayjs().subtract(8, 'day').format('YYYY-MM-DD'),
                channel: 'b2b',
                customerName: 'Ottica & Udito Campania Srl',
                customerFiscalCode: '',
                customerVatNumber: '03987654321',
                lines: [
                    {
                        id: 'act-ddt-001-line-1',
                        description: 'Apparecchio acustico Oticon Intent livello 2',
                        articleCode: 'OTI-INTENT',
                        serialId: 'serial-oticon-001',
                        quantity: 1,
                        unitPrice: 1800.00,
                        vatRate: 22,
                        lineTotal: 1800.00,
                    },
                    {
                        id: 'act-ddt-001-line-2',
                        description: 'Charger Fast',
                        articleCode: 'CHG-FAST',
                        serialId: null,
                        quantity: 1,
                        unitPrice: 120.00,
                        vatRate: 22,
                        lineTotal: 120.00,
                    },
                ],
                netAmount: 1920.00,
                vatAmount: 422.40,
                totalAmount: 2342.40,
                status: 'emesso',
                linkedInvoiceId: null,
                notes: 'Consegna sede cliente – ordine OAU-2026-060.',
                issuedAt: dayjs().subtract(8, 'day').toISOString(),
            },
            {
                id: 'act-ddt-002',
                companyDocumentId: 'aurifon',
                ddtNumber: `DDT-${dayjs().year()}-002`,
                issueDate: dayjs().subtract(5, 'day').format('YYYY-MM-DD'),
                channel: 'b2b',
                customerName: 'Ottica & Udito Campania Srl',
                customerFiscalCode: '',
                customerVatNumber: '03987654321',
                lines: [
                    {
                        id: 'act-ddt-002-line-1',
                        description: 'Consulenza audiologica specialistica – 2 sessioni',
                        articleCode: null,
                        serialId: null,
                        quantity: 2,
                        unitPrice: 180.00,
                        vatRate: 22,
                        lineTotal: 360.00,
                    },
                ],
                netAmount: 360.00,
                vatAmount: 79.20,
                totalAmount: 439.20,
                status: 'emesso',
                linkedInvoiceId: null,
                notes: 'Servizi aggiuntivi contratto OAU.',
                issuedAt: dayjs().subtract(5, 'day').toISOString(),
            },
            {
                id: 'act-ddt-003',
                companyDocumentId: 'aurifon',
                ddtNumber: `DDT-${dayjs().year()}-003`,
                issueDate: dayjs().subtract(3, 'day').format('YYYY-MM-DD'),
                channel: 'b2c',
                customerName: 'Gennaro Iaccarino',
                customerFiscalCode: 'CCRGNN68M10F839P',
                customerVatNumber: '',
                lines: [
                    {
                        id: 'act-ddt-003-line-1',
                        description: 'Oticon Intent livello 2 – consegna definitiva',
                        articleCode: 'OTI-INTENT',
                        serialId: 'serial-oticon-001',
                        quantity: 1,
                        unitPrice: 1710.00,
                        vatRate: 22,
                        lineTotal: 1710.00,
                    },
                ],
                netAmount: 1710.00,
                vatAmount: 376.20,
                totalAmount: 2086.20,
                status: 'emesso',
                linkedInvoiceId: null,
                notes: 'Consegna protesi acustica post-accordo commerciale.',
                issuedAt: dayjs().subtract(3, 'day').toISOString(),
            },
            {
                id: 'act-ddt-004',
                companyDocumentId: 'aurifon',
                ddtNumber: `DDT-${dayjs().year()}-004`,
                issueDate: dayjs().subtract(12, 'day').format('YYYY-MM-DD'),
                channel: 'b2b',
                customerName: 'Studio Medico Associato Vesuvio',
                customerFiscalCode: '',
                customerVatNumber: '08112233445',
                lines: [
                    {
                        id: 'act-ddt-004-line-1',
                        description: 'Servizio audiometria – contratto annuale (acconto)',
                        articleCode: null,
                        serialId: null,
                        quantity: 1,
                        unitPrice: 2400.00,
                        vatRate: 22,
                        lineTotal: 2400.00,
                    },
                ],
                netAmount: 2400.00,
                vatAmount: 528.00,
                totalAmount: 2928.00,
                status: 'fatturato',
                linkedInvoiceId: 'b2b-inv-003',
                notes: 'DDT collegato alla fattura b2b-inv-003.',
                issuedAt: dayjs().subtract(12, 'day').toISOString(),
            },
        ],
    });
};

const createArchiveDocumentsSeed = () => {
    const currentYear = dayjs().year();
    const archiveYears = Array.from({ length: 10 }, (_, i) => currentYear - 10 + i);
    const result = [];

    archiveYears.forEach((year) => {
        result.push({
            id: `archive-sup-${year}`,
            title: `Fattura fornitore principale ${year}`,
            type: 'supplier_invoice',
            status: 'contabilizzata',
            companyDocumentId: 'aurifon',
            counterpartyName: 'Deman Srl',
            patientName: null,
            amount: 1800 + (year - (currentYear - 10)) * 120,
            dueDate: `${year}-03-15`,
            fiscalYear: year,
            domain: 'finance',
            route: '/finance/passivo',
            referenceNumber: `ARC-${year}-001`,
            tags: ['archivio', 'chiuso'],
            updatedAt: `${year}-04-30T10:00:00.000Z`,
            notes: [],
        });
        result.push({
            id: `archive-prat-${year}`,
            title: `Pratiche ASL ${year}`,
            type: 'practice',
            status: 'fatturata',
            companyDocumentId: 'aurifon',
            counterpartyName: 'ASL Napoli 3 Sud',
            patientName: null,
            amount: 5200 + (year - (currentYear - 10)) * 200,
            dueDate: `${year}-06-30`,
            fiscalYear: year,
            domain: 'commerciale',
            route: '/finance/documentale',
            referenceNumber: `PRAT-ASL-${year}`,
            tags: ['archivio', 'asl', 'chiuso'],
            updatedAt: `${year}-07-15T10:00:00.000Z`,
            notes: [],
        });
        if (year >= currentYear - 6) {
            result.push({
                id: `archive-sts-${year}`,
                title: `Export STS annuale ${year}`,
                type: 'sts_export',
                status: 'emessa',
                companyDocumentId: 'aurifon',
                counterpartyName: 'Commercialista',
                patientName: null,
                amount: 0,
                dueDate: `${year}-01-31`,
                fiscalYear: year,
                domain: 'compliance',
                route: '/finance/compliance',
                referenceNumber: `STS-${year}`,
                tags: ['archivio', 'sts', 'chiuso'],
                updatedAt: `${year}-02-15T10:00:00.000Z`,
                notes: [],
            });
        }
    });

    return result;
};

const createMigrationWorkspaceSeed = () => ({
    activeMigrationId: null,
    migrations: [],
});

const createTemplateEditorWorkspaceSeed = () => {    const templates = [
        {
            id: 'template-mds-ddt-01',
            name: 'Bolla MDS standard',
            type: 'mds_ddt',
            description: 'Modello standard per DDT MDS con campi paziente, dispositivo e importo ASL.',
            status: 'attivo',
            companyDocumentId: 'medical-audio',
            fieldMappings: (TEMPLATE_FIELD_PRESETS.mds_ddt || []).map((f) => ({ ...f })),
            createdAt: dayjs().subtract(30, 'day').toISOString(),
            updatedAt: dayjs().subtract(2, 'day').toISOString(),
        },
        {
            id: 'template-asl-rf-01',
            name: 'Richiesta fornitura ASL – standard',
            type: 'asl_richiesta_fornitura',
            description: 'Modulo ASL per richiesta di fornitura protesi acustiche.',
            status: 'attivo',
            companyDocumentId: 'aurifon',
            fieldMappings: (TEMPLATE_FIELD_PRESETS.asl_richiesta_fornitura || []).map((f) => ({ ...f })),
            createdAt: dayjs().subtract(20, 'day').toISOString(),
            updatedAt: dayjs().subtract(1, 'day').toISOString(),
        },
        {
            id: 'template-asl-pc-01',
            name: 'Piano di cessione ASL',
            type: 'asl_piano_cessione',
            description: 'Piano di cessione per pratiche ASL con quote privato e ASL.',
            status: 'bozza',
            companyDocumentId: 'aurifon',
            fieldMappings: (TEMPLATE_FIELD_PRESETS.asl_piano_cessione || []).map((f) => ({ ...f })),
            createdAt: dayjs().subtract(5, 'day').toISOString(),
            updatedAt: dayjs().subtract(5, 'day').toISOString(),
        },
        {
            id: 'template-asl-rc-01',
            name: 'Ricevuta consegna ASL',
            type: 'asl_ricevuta_consegna',
            description: 'Ricevuta da far firmare al paziente al momento della consegna.',
            status: 'attivo',
            companyDocumentId: 'medical-audio',
            fieldMappings: (TEMPLATE_FIELD_PRESETS.asl_ricevuta_consegna || []).map((f) => ({ ...f })),
            createdAt: dayjs().subtract(15, 'day').toISOString(),
            updatedAt: dayjs().subtract(3, 'day').toISOString(),
        },
    ];

    return recalculateTemplateEditorWorkspace({
        activeTemplateId: templates[0].id,
        templates,
    });
};

const createAccountantPackageWorkspaceSeed = () => {
    const q1Year = dayjs().year();
    const q1Start = dayjs().year(q1Year).month(0).date(1);
    const registroAcquisti = [
        {
            id: 'acq-seed-001',
            date: q1Start.add(58, 'day').format('YYYY-MM-DD'),
            numero: 'Reg.1/184',
            fornitore: 'Deman Srl',
            descrizione: 'Fattura n. 1875/2026',
            imponibile: 1149.20,
            aliquota: 22,
            iva: 252.82,
            totale: 1402.02,
            tipo: 'fattura_acquisto',
        },
        {
            id: 'acq-seed-002',
            date: q1Start.add(59, 'day').format('YYYY-MM-DD'),
            numero: 'Reg.1/185',
            fornitore: 'Energia Campania',
            descrizione: 'Fattura n. ENE-031',
            imponibile: 412.72,
            aliquota: 22,
            iva: 90.80,
            totale: 503.52,
            tipo: 'fattura_acquisto',
        },
    ];
    const registroVendite = [
        {
            id: 'vend-seed-001',
            date: q1Start.add(15, 'day').format('YYYY-MM-DD'),
            numero: 'FT-B2B-001/2026',
            cliente: 'Clinica Audiologica Sud Srl',
            descrizione: 'Fattura FT-B2B-001/2026',
            imponibile: 860.00,
            aliquota: 22,
            iva: 189.20,
            totale: 1049.20,
            tipo: 'fattura_b2b',
        },
        {
            id: 'vend-seed-002',
            date: q1Start.add(50, 'day').format('YYYY-MM-DD'),
            numero: 'FT-B2B-002/2026',
            cliente: 'Ottica & Udito Campania Srl',
            descrizione: 'Fattura FT-B2B-002/2026',
            imponibile: 3600.00,
            aliquota: 22,
            iva: 792.00,
            totale: 4392.00,
            tipo: 'fattura_b2b',
        },
        {
            id: 'vend-seed-003',
            date: q1Start.add(52, 'day').format('YYYY-MM-DD'),
            numero: 'FT-LIB-001/2026',
            cliente: 'Mario Esposito',
            descrizione: 'Fattura libera FT-LIB-001/2026',
            imponibile: 180.00,
            aliquota: 22,
            iva: 39.60,
            totale: 219.60,
            tipo: 'fattura_libera',
        },
    ];
    const summaryByAliquota = [
        {
            aliquota: 22,
            imponibileAcquisti: 1561.92,
            ivaAcquisti: 343.62,
            imponibileVendite: 4640.00,
            ivaVendite: 1020.80,
        },
    ];
    const totali = {
        imponibileVendite: 4640.00,
        ivaVendite: 1020.80,
        imponibileAcquisti: 1561.92,
        ivaAcquisti: 343.62,
        liquidazioneIva: 677.18,
    };
    const pkg = {
        id: `pkg-q1-${q1Year}-seed`,
        companyDocumentId: 'aurifon',
        quarter: 'Q1',
        year: q1Year,
        period: `Q1 ${q1Year}`,
        status: 'pronto',
        generatedAt: dayjs().subtract(3, 'day').toISOString(),
        sentAt: null,
        registroAcquisti,
        registroVendite,
        summaryByAliquota,
        totali,
    };
    return {
        activePackageId: pkg.id,
        packages: [pkg],
    };
};

export const createRoadmapWorkspaceSeed = ({
    roleName,
    activeCompany,
    companies,
}) => {
    const normalizedCompanies = buildCompanies(companies);
    const activeCompanyDocumentId = resolveCompanyDocumentId(activeCompany) || normalizedCompanies[0]?.documentId || 'medical-audio';
    const roleKey = resolveRoadmapBusinessRole(roleName);

    const passiveWorkspace = createPassiveWorkspaceSeed();
    const inventoryWorkspace = createInventoryWorkspaceSeed();
    const foreignInvoiceWorkspace = createForeignInvoiceWorkspaceSeed();
    const activeWorkspace = createActiveWorkspaceSeed();
    const documents = [
        createDocument({
            id: 'doc-practice-med-01',
            title: 'Pratica ASL Medical Audio – Carlo Ferraro',
            type: 'practice',
            status: 'da_fatturare',
            companyDocumentId: 'medical-audio',
            patientName: 'Carlo Ferraro',
            counterpartyName: 'ASL Salerno 1',
            amount: 1151.00,
            dueDate: dayjs().add(5, 'day').format('YYYY-MM-DD'),
            domain: 'commerciale',
            route: '/finance/documentale?doc=doc-practice-med-01',
            referenceNumber: 'PRAT-ASL-MED-001',
            tags: ['asl', 'medical-audio', 'migrazione'],
        }),
        ...PASSIVE_DOCUMENTS,
        createDocument({
            id: 'doc-practice-aur-08',
            title: 'Pratica ASL Aurifon - Riconducibile',
            type: 'practice',
            status: 'da_fatturare',
            companyDocumentId: 'aurifon',
            patientName: 'Rosa De Luca',
            counterpartyName: 'ASL Napoli 3 Sud',
            amount: 1151,
            dueDate: dayjs().add(3, 'day').format('YYYY-MM-DD'),
            domain: 'commerciale',
            route: '/finance/documentale?doc=doc-practice-aur-08',
            referenceNumber: 'PRAT-ASL-008',
            tags: ['asl', 'riconducibile', 'rate'],
        }),
        createDocument({
            id: 'doc-order-nso-441',
            title: 'Ordine NSO 441',
            type: 'nso_order',
            status: 'ricevuto',
            companyDocumentId: 'aurifon',
            patientName: 'Gennaro Iaccarino',
            counterpartyName: 'ASL Napoli 1',
            amount: 0,
            dueDate: dayjs().add(1, 'day').format('YYYY-MM-DD'),
            domain: 'compliance',
            route: '/finance/documentale?doc=doc-order-nso-441',
            referenceNumber: 'NSO-441',
            tags: ['nso', 'ordine', 'presa-in-carico'],
        }),
        createDocument({
            id: 'doc-credit-2026-09',
            title: 'Nota di credito pratica assicurazione',
            type: 'customer_invoice',
            status: 'emessa',
            companyDocumentId: 'medical-audio',
            patientName: 'Anna Cuomo',
            counterpartyName: 'Anna Cuomo',
            amount: -2690,
            dueDate: dayjs().subtract(2, 'day').format('YYYY-MM-DD'),
            domain: 'commerciale',
            route: '/finance/documentale?doc=doc-credit-2026-09',
            referenceNumber: 'NC-09/2026',
            tags: ['storno', 'assicurazione', 'cross-company'],
        }),
        createDocument({
            id: 'doc-cross-export-sts',
            title: 'Export STS privati trimestre Q1',
            type: 'sts_export',
            status: 'pronta_export',
            companyDocumentId: 'aurifon',
            counterpartyName: 'Commercialista',
            amount: 0,
            dueDate: dayjs().add(10, 'day').format('YYYY-MM-DD'),
            domain: 'compliance',
            route: '/finance/documentale?doc=doc-cross-export-sts',
            referenceNumber: 'STS-Q1',
            tags: ['sts', 'export', 'privacy'],
        }),
    ];

    const playbooks = [
        {
            id: 'playbook-passivo',
            title: 'Hai ricevuto una fattura fornitore?',
            description: 'Controlla DDT, verifica prezzi, rettifica eventuali servizi e manda in prima nota.',
            companyDocumentId: 'aurifon',
            relatedDocumentId: null,
            steps: [
                { id: 'p1', title: 'Apri XML/PDF e verifica il fornitore', status: 'done' },
                { id: 'p2', title: 'Aggancia i DDT aperti o conferma la proposta automatica', status: 'current' },
                { id: 'p3', title: 'Rivedi totali, IVA e tolleranze prima della contabilizzazione', status: 'todo' },
            ],
        },
        {
            id: 'playbook-trial-sale',
            title: 'Da contovisione a fattura',
            description: 'Chiudi accordo, genera pratica da fatturare e prepara le scadenze senza toccare il backend.',
            companyDocumentId: 'aurifon',
            relatedDocumentId: 'doc-practice-aur-08',
            steps: [
                { id: 't1', title: 'Conferma accordo commerciale e quota ASL', status: 'done' },
                { id: 't2', title: 'Avvisa amministrazione con task interno', status: 'current' },
                { id: 't3', title: 'Genera fattura o nota di credito collegata', status: 'todo' },
            ],
        },
    ];

    const helpArticles = [
        {
            id: 'help-rbac',
            title: 'Come leggere i permessi nel mock frontend',
            body: 'Admin vede vista completa, Contabile vede costi e quadrature, Commerciale lavora su pratiche e stati operativi senza dati bancari.',
        },
        {
            id: 'help-sync',
            title: 'Cosa succede se la linea cade',
            body: 'Le modifiche restano in coda locale, cambiano lo stato della UI e vengono sincronizzate appena rimetti la modalità online.',
        },
        {
            id: 'help-cross-company',
            title: 'Come si muove una pratica tra Medical Audio e Aurifon',
            body: 'La ricerca e il cruscotto tengono il collegamento cross-company via referenceNumber e notificano il team quando cambia il lato contabile della pratica.',
        },
    ];

    const notifications = [
        {
            id: 'notif-1',
            level: 'warning',
            unread: true,
            title: '2 fatture passive da esaminare',
            description: 'Aurifon ha documenti SDI non ancora contabilizzati.',
            route: '/finance/documentale',
            companyDocumentId: 'aurifon',
            createdAt: dayjs().subtract(2, 'hour').toISOString(),
        },
        {
            id: 'notif-2',
            level: 'info',
            unread: true,
            title: 'Pratica ASL pronta per fattura',
            description: 'Rosa De Luca ha chiuso l’accordo: passa in amministrazione.',
            route: '/finance/processi?playbook=playbook-trial-sale',
            companyDocumentId: 'aurifon',
            createdAt: dayjs().subtract(5, 'hour').toISOString(),
        },
        {
            id: 'notif-3',
            level: 'critical',
            unread: false,
            title: 'Nota di credito cross-company da verificare',
            description: 'Medical Audio ha stornato una fattura collegata a un rimborso assicurativo.',
            route: '/finance/documentale?doc=doc-credit-2026-09',
            companyDocumentId: 'medical-audio',
            createdAt: dayjs().subtract(1, 'day').toISOString(),
        },
    ];

    return {
        activeCompanyDocumentId,
        businessRole: roleKey,
        companies: normalizedCompanies,
        documents,
        archiveDocuments: createArchiveDocumentsSeed(),
        selectedFiscalYears: [dayjs().year()],
        passiveWorkspace,
        treasuryWorkspace: createTreasuryWorkspaceSeed({ passiveWorkspace }),
        inventoryWorkspace,
        foreignInvoiceWorkspace,
        activeWorkspace,
        complianceWorkspace: createComplianceWorkspaceSeed(),
        billingWorkspace: createBillingWorkspaceSeed(),
        migrationWorkspace: createMigrationWorkspaceSeed(),
        templateEditorWorkspace: createTemplateEditorWorkspaceSeed(),
        accountantPackageWorkspace: createAccountantPackageWorkspaceSeed(),
        notifications,
        playbooks,
        helpArticles,
        pendingSaves: [],
        networkMode: 'online',
        lastSyncedAt: dayjs().toISOString(),
        loadedAt: dayjs().toISOString(),
    };
};

export const getStoredRoadmapWorkspace = () => {
    try {
        const raw = localStorage.getItem(ROADMAP_FINANCE_STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch (error) {
        return null;
    }
};

export const persistRoadmapWorkspace = (state) => {
    try {
        localStorage.setItem(ROADMAP_FINANCE_STORAGE_KEY, JSON.stringify({
            activeCompanyDocumentId: state.activeCompanyDocumentId,
            businessRole: state.businessRole,
            companies: state.companies,
            documents: state.documents,
            selectedFiscalYears: state.selectedFiscalYears,
            closedFiscalYears: state.closedFiscalYears,
            passiveWorkspace: state.passiveWorkspace,
            treasuryWorkspace: state.treasuryWorkspace,
            inventoryWorkspace: state.inventoryWorkspace,
            foreignInvoiceWorkspace: state.foreignInvoiceWorkspace,
            activeWorkspace: state.activeWorkspace,
            complianceWorkspace: state.complianceWorkspace,
            billingWorkspace: state.billingWorkspace,
            migrationWorkspace: state.migrationWorkspace,
            templateEditorWorkspace: state.templateEditorWorkspace,
            accountantPackageWorkspace: state.accountantPackageWorkspace,
            notifications: state.notifications,
            playbooks: state.playbooks,
            helpArticles: state.helpArticles,
            pendingSaves: state.pendingSaves,
            billingHubPassive: state.billingHubPassive,
            networkMode: state.networkMode,
            lastSyncedAt: state.lastSyncedAt,
            loadedAt: state.loadedAt,
        }));
    } catch (error) {
        console.warn('[roadmapFinance] persist error', error);
    }
};

const hasTreasuryWorkspaceData = (workspace) => {
    return Boolean(workspace) && (
        (workspace.sources || []).length > 0 ||
        (workspace.payables || []).length > 0 ||
        (workspace.bankMovements || []).length > 0
    );
};

const hasInventoryWorkspaceData = (workspace) => {
    return Boolean(workspace) && (
        (workspace.deposits || []).length > 0 ||
        (workspace.serials || []).length > 0 ||
        (workspace.articles || []).length > 0
    );
};

const hasForeignInvoiceWorkspaceData = (workspace) => {
    return Boolean(workspace) && (workspace.foreignInvoices || []).length > 0;
};

const hasActiveWorkspaceData = (workspace) => {
    return Boolean(workspace) && (
        (workspace.agreements || []).length > 0 ||
        (workspace.operativeQueue || []).length > 0
    );
};

const hasComplianceWorkspaceData = (workspace) => {
    return Boolean(workspace) && (
        (workspace.nsoOrders || []).length > 0 ||
        (workspace.pratiche || []).length > 0
    );
};

const hasBillingWorkspaceData = (workspace) => {
    return Boolean(workspace) && (
        (workspace.b2bInvoices || []).length > 0 ||
        (workspace.registriSezionali || []).length > 0
    );
};

export const mergeRoadmapWorkspace = (seed, persisted) => {
    if (!persisted) {
        return seed;
    }

    const mergedDocuments = Array.isArray(persisted.documents) && persisted.documents.length > 0
        ? persisted.documents
        : seed.documents;
    const mergedNotifications = Array.isArray(persisted.notifications) && persisted.notifications.length > 0
        ? persisted.notifications
        : seed.notifications;
    const mergedPlaybooks = Array.isArray(persisted.playbooks) && persisted.playbooks.length > 0
        ? persisted.playbooks
        : seed.playbooks;
    const mergedHelpArticles = Array.isArray(persisted.helpArticles) && persisted.helpArticles.length > 0
        ? persisted.helpArticles
        : seed.helpArticles;
    const mergedPassiveWorkspace = recalculatePassiveWorkspace(seed.passiveWorkspace);
    const mergedInventoryWorkspace = recalculateInventoryWorkspace({
        inventoryWorkspace: hasInventoryWorkspaceData(persisted.inventoryWorkspace)
            ? persisted.inventoryWorkspace
            : seed.inventoryWorkspace,
    });
    const mergedTreasuryWorkspace = recalculateTreasuryWorkspace({
        treasuryWorkspace: hasTreasuryWorkspaceData(persisted.treasuryWorkspace)
            ? persisted.treasuryWorkspace
            : seed.treasuryWorkspace,
        passiveWorkspace: mergedPassiveWorkspace,
        documents: mergedDocuments,
    });
    const mergedForeignInvoiceWorkspace = hasForeignInvoiceWorkspaceData(persisted.foreignInvoiceWorkspace)
        ? persisted.foreignInvoiceWorkspace
        : seed.foreignInvoiceWorkspace;
    const mergedActiveWorkspace = recalculateActiveWorkspace(
        hasActiveWorkspaceData(persisted.activeWorkspace)
            ? persisted.activeWorkspace
            : seed.activeWorkspace
    );
    const mergedComplianceWorkspace = recalculateComplianceWorkspace(
        hasComplianceWorkspaceData(persisted.complianceWorkspace)
            ? persisted.complianceWorkspace
            : seed.complianceWorkspace
    );
    const mergedBillingWorkspace = recalculateBillingWorkspace(
        hasBillingWorkspaceData(persisted.billingWorkspace)
            ? {
                ...persisted.billingWorkspace,
                registriSezionali: (persisted.billingWorkspace?.registriSezionali || []).length > 0
                    ? persisted.billingWorkspace.registriSezionali
                    : seed.billingWorkspace.registriSezionali,
            }
            : seed.billingWorkspace
    );

    const mergedMigrationWorkspace = (persisted.migrationWorkspace && Array.isArray(persisted.migrationWorkspace.migrations))
        ? persisted.migrationWorkspace
        : seed.migrationWorkspace;

    const mergedTemplateEditorWorkspace = (persisted.templateEditorWorkspace && Array.isArray(persisted.templateEditorWorkspace.templates))
        ? recalculateTemplateEditorWorkspace(persisted.templateEditorWorkspace)
        : seed.templateEditorWorkspace;

    const mergedAccountantPackageWorkspace = (persisted.accountantPackageWorkspace && Array.isArray(persisted.accountantPackageWorkspace.packages))
        ? persisted.accountantPackageWorkspace
        : seed.accountantPackageWorkspace;

    return {
        ...seed,
        ...persisted,
        activeCompanyDocumentId: seed.activeCompanyDocumentId || persisted.activeCompanyDocumentId,
        businessRole: seed.businessRole,
        companies: seed.companies,
        documents: mergedDocuments,
        archiveDocuments: seed.archiveDocuments,
        selectedFiscalYears: Array.isArray(persisted?.selectedFiscalYears) && persisted.selectedFiscalYears.length > 0
            ? persisted.selectedFiscalYears
            : seed.selectedFiscalYears,
        notifications: mergedNotifications,
        playbooks: mergedPlaybooks,
        helpArticles: mergedHelpArticles,
        passiveWorkspace: mergedPassiveWorkspace,
        treasuryWorkspace: mergedTreasuryWorkspace,
        inventoryWorkspace: mergedInventoryWorkspace,
        foreignInvoiceWorkspace: mergedForeignInvoiceWorkspace,
        activeWorkspace: mergedActiveWorkspace,
        complianceWorkspace: mergedComplianceWorkspace,
        billingWorkspace: mergedBillingWorkspace,
        migrationWorkspace: mergedMigrationWorkspace,
        templateEditorWorkspace: mergedTemplateEditorWorkspace,
        accountantPackageWorkspace: mergedAccountantPackageWorkspace,
    };
};
